/**
 * Ask to change (slice 2d spec §10.5, decision 8): the side panel opens on
 * the doc's chat with `?attach=<doc id>`, and the panel's composer starts
 * with the doc's chip (removable) and "Update this doc: " typed in, focused.
 * Sending posts the message with `doc_id` (§11.4) and drops the chip. In a
 * new chat it all stays a draft in the composer until Send makes the thread.
 */
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** The words typed in for the person to finish. */
export const ASK_TEXT = "Update this doc: ";

export type Attach = {
  /** The attached doc, while its chip is on. */
  docId: string | null;
  /**
   * The composer's key: a new Ask to change remounts it with the chip and
   * the text, while removing the chip keeps what's been typed.
   */
  key: string;
  clear: () => void;
};

/** The attach the composer was last keyed for, and how many there have been. */
export type AttachSeen = { docId: string | null; n: number };

/**
 * A doc attached where none was (or another doc) is a new Ask to change: a
 * new key remounts the composer with the chip, the words and the focus, the
 * same doc again after its chip was removed too (Devin on #275). Removing the
 * chip keeps the key, so what's been typed stays.
 */
export function nextAttach(seen: AttachSeen, docId: string | null): AttachSeen {
  return { docId, n: docId ? seen.n + 1 : seen.n };
}

export function useAttach(): Attach {
  const [params, setParams] = useSearchParams();
  const docId = params.get("attach");
  // React's "a value from the previous render" pattern: each new attach is a new key.
  const [seen, setSeen] = useState<AttachSeen>({ docId, n: 0 });
  if (docId !== seen.docId) setSeen(nextAttach(seen, docId));
  const key = `attach-${seen.n}`;
  const clear = useCallback(
    () =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.delete("attach");
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  return { docId, key, clear };
}
